/*
 * ez-cart-memory.js — device-side cart memory + the 15-17 Aug Azadi rescue popup.
 *
 * WHY THIS EXISTS (discussions:atc-capture-and-followup, owner-decided 2026-08-11):
 *   An add-to-cart is anonymous and 0 of 571 customers hold WhatsApp/SMS marketing consent, so we
 *   cannot MESSAGE a cart-adder. Instead the DEVICE remembers what it added, and when that same
 *   device comes back after the half-price day has passed we show it the private rescue code.
 *   The sale is the prize; identity arrives for free at checkout. Nothing is ever messaged, so no
 *   consent problem exists anywhere in this design.
 *
 * THREE PHASES
 *   1 (ship -> 14 Aug)  every add-to-cart is recorded device-side against a generated anonymous id.
 *   2 (14 Aug)          a device holding a phase-1 record that visits on the 14th is TAGGED, with its
 *                       own GA4 event, so the funnel is measurable and not merely functional.
 *   3 (15-17 Aug)       the AZADI1400 offer popup on the cart page.
 *                       AMENDED 15 Aug 00:20 PKT by owner ruling: this was a RESCUE aimed at the
 *                       phase-1/2 pool. It is now open to EVERY visitor who reaches the cart with
 *                       something in it -- the 50% ad creative is still running and pulling far
 *                       more people than that pool, all of whom arrive expecting an offer. Phases
 *                       1 and 2 are untouched and still recording; the record simply stopped being
 *                       the display gate. Full ruling + the reasoning: the header of
 *                       snippets/ez-cart-memory.liquid.
 *
 * DISCOUNT-CODE-ON-PURCHASE, A HEURISTIC (owner ask, 2026-08-14, PERMANENT / not date-gated):
 *   "so redemption ties through to actual revenue rather than stopping at the cart." The
 *   THEME has no file for the order-status/thank-you page at all in OS 2.0 checkout
 *   extensibility -- that page is Shopify-hosted, a separate origin, outside business/storefront/
 *   entirely. A TRUE purchase-event enrichment needs a Shopify Custom Pixel subscribing to
 *   checkout_completed (an Admin mutation, previously identified and left for the owner's go --
 *   see activity:atc-capture-and-followup, "the authoritative upgrade"). That is out of
 *   theme-builder's lane and is NOT built here.
 *   What IS built here, in-lane: this file already runs a heuristic purchase DETECTOR
 *   (resolvePurchase, below) for the rescue-suppression feature. It now also remembers which
 *   discount code(s) were on the cart (rec.last_discount_codes, fed by cart_discount_codes in the
 *   config JSON -- the exact same derivation cart-summary.liquid uses) and, at the SAME moment
 *   resolvePurchase infers a completed order, fires ez_discount_purchase_heuristic carrying that
 *   code. It is named "_heuristic" ON PURPOSE and must never be read as a guaranteed 1:1 purchase
 *   event -- it inherits the exact same confidence and failure modes as ez_purchase_suppressed
 *   below (a strong signal, not an authoritative one).
 *
 * FAIL-CLOSED, TWICE OVER
 *   - Phase 3 renders NOTHING unless the merchant-set minimum cart value is > 0 (Theme settings).
 *     The margin number was outstanding at build time; an unset threshold must not leak a discount.
 *   - The whole file is wrapped so that ANY throw dies silently. A broken popup must never cost an
 *     order during the biggest week of the year, so nothing here is allowed to reach add-to-cart or
 *     checkout: the fetch wrapper returns the ORIGINAL promise untouched and only observes a clone.
 *
 * TWO SIGNALS, ON PURPOSE (both, not either)
 *   - Shopify's OWN cart is the primary "this device has an abandoned cart" signal. Server-side,
 *     storage-independent, 14-day cookie refreshed on every cart update -- so it spans 11->17 Aug
 *     and survives Safari's script-writable-storage cap, which localStorage does not.
 *   - localStorage carries what Shopify cannot give us: the anonymous id, the phase-1 timestamp,
 *     the phase-2 tag, the shown/dismissed counters.
 *   The cart signal also BACK-FILLS a record for anyone who added before this file shipped.
 *
 * NO external libraries, no external requests, no added network calls.
 */
(function () {
  'use strict';

  var KEY = 'ez_cm_v1';
  /* Separate one-key flag so a future Shopify Custom Pixel (which reaches top-frame storage via the
     Web Pixels `browser.localStorage` API) can mark a purchase without knowing this record's shape. */
  var KEY_PURCHASED = 'ez_cm_purchased';
  var SESSION_SHOWN = 'ez_cm_shown_session';
  var MAX_VARIANTS = 25;

  var CFG = null;
  var rec = null;
  var store = null;
  var session = null;

  /* ---------------------------------------------------------------- storage */

  function probe(which) {
    try {
      var s = window[which];
      var t = '__ezcm_probe';
      s.setItem(t, '1');
      s.removeItem(t);
      return s;
    } catch (e) {
      return null; /* private mode, blocked storage, quota -- degrade to no-op */
    }
  }

  function read() {
    if (!store) return null;
    try {
      var raw = JSON.parse(store.getItem(KEY) || 'null');
      return raw && typeof raw === 'object' && raw.id ? raw : null;
    } catch (e) {
      return null;
    }
  }

  function save() {
    if (!store || !rec) return;
    try {
      rec.updated = new Date().toISOString();
      store.setItem(KEY, JSON.stringify(rec));
    } catch (e) {}
  }

  function makeId() {
    var hex = '';
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        var a = new Uint8Array(8);
        window.crypto.getRandomValues(a);
        for (var i = 0; i < a.length; i++) hex += ('0' + a[i].toString(16)).slice(-2);
      }
    } catch (e) {}
    while (hex.length < 16) hex += Math.random().toString(16).slice(2);
    return 'ez-' + hex.slice(0, 16) + '-' + Date.now().toString(36);
  }

  /* ------------------------------------------------------------------ dates */

  /* The SHOP's own clock is authoritative -- CFG.today is rendered server-side by Liquid in the shop
     timezone (Asia/Karachi), so a device with a wrong clock cannot mis-fire a phase. The UTC+5
     computation below is a fallback only, for the case where the Liquid value failed to render. */
  function today() {
    if (CFG && /^\d{4}-\d{2}-\d{2}$/.test(CFG.today || '')) return CFG.today;
    return new Date(Date.now() + 5 * 3600 * 1000).toISOString().slice(0, 10);
  }

  function dayOf(iso) {
    if (!iso) return '';
    try {
      return new Date(new Date(iso).getTime() + 5 * 3600 * 1000).toISOString().slice(0, 10);
    } catch (e) {
      return '';
    }
  }

  /* ------------------------------------------------------------------- GA4 */

  /* gtag may not be defined yet when this deferred file runs. Registration of GA4 custom dimensions
     is forward-only, so a dropped event is permanently invisible -- queue and drain rather than
     firing into the void. */
  var queue = [];
  var drains = 0;

  function drain() {
    if (typeof window.gtag !== 'function' && !(window.dataLayer && window.dataLayer.push)) {
      if (++drains > 24) return; /* ~12s, then give up quietly */
      window.setTimeout(drain, 500);
      return;
    }
    while (queue.length) {
      var q = queue.shift();
      try {
        if (typeof window.gtag === 'function') window.gtag('event', q[0], q[1]);
        else window.dataLayer.push(mergeEvent(q[0], q[1]));
      } catch (e) {}
    }
  }

  function mergeEvent(name, params) {
    var out = { event: name };
    for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) out[k] = params[k];
    return out;
  }

  function ga(name, params) {
    try {
      var p = params || {};
      if (rec && rec.id) p.ez_device_id = rec.id;
      /* `value` + `currency` are STANDARD GA4 params -- they are queryable with no custom-dimension
         registration at all, which is the hedge against forward-only registration. */
      if (typeof p.ez_cart_value === 'number') {
        p.value = Math.round(p.ez_cart_value) / 100;
        p.currency = (CFG && CFG.currency) || 'PKR';
      }
      queue.push([name, p]);
      drain();
    } catch (e) {}
  }

  /* -------------------------------------------------------------- the record */

  function seed(source, value) {
    var now = new Date().toISOString();
    rec = {
      v: 1,
      id: makeId(),
      first_seen: now,
      first_seen_day: today(),
      updated: now,
      source: source,
      adds: 0,
      variants: [],
      cart_value: typeof value === 'number' && value > 0 ? value : 0,
      max_cart_value: typeof value === 'number' && value > 0 ? value : 0,
      accessed_on_14: false,
      accessed_on_14_at: null,
      checkout_started: false,
      checkout_started_at: null,
      purchased: false,
      purchased_at: null,
      last_discount_codes: [],
      rescue_shown: 0,
      rescue_last_shown: null,
      rescue_dismissed: 0,
      rescue_applied: false
    };
    save();
    ga('ez_cart_memory_start', {
      ez_cart_value: rec.cart_value,
      ez_source: source,
      ez_first_seen_day: rec.first_seen_day
    });
  }

  function touchCartValue(minorUnits) {
    if (!rec || typeof minorUnits !== 'number' || minorUnits < 0) return;
    rec.cart_value = minorUnits;
    if (minorUnits > (rec.max_cart_value || 0)) rec.max_cart_value = minorUnits;
  }

  /* Remembers the LAST NON-EMPTY set of discount codes seen on this device's cart. Deliberately
     never overwrites with an empty array -- a code applied on the cart page and still present when
     checkout starts is what we want to remember even if a later (unrelated) render race ever saw
     an empty list first. This is the device-side half of ez_discount_purchase_heuristic. */
  function touchDiscountCodes(codes) {
    if (!rec || !Array.isArray(codes) || !codes.length) return;
    rec.last_discount_codes = codes.slice(0, 5);
  }

  /* --------------------------------------------------------- add-to-cart tap */

  function isAddUrl(u) {
    try {
      var s = typeof u === 'string' ? u : (u && (u.url || String(u))) || '';
      return s.indexOf('/cart/add') !== -1;
    } catch (e) {
      return false;
    }
  }

  function recordAdd(payload) {
    try {
      if (!rec) seed('add_to_cart');
      rec.adds = (rec.adds || 0) + 1;

      var items = [];
      if (payload && Array.isArray(payload.items)) items = payload.items;
      else if (payload && (payload.id || payload.variant_id)) items = [payload];

      var added = 0;
      for (var i = 0; i < items.length; i++) {
        var it = items[i] || {};
        var vid = String(it.variant_id || it.id || '');
        if (vid && rec.variants.indexOf(vid) === -1 && rec.variants.length < MAX_VARIANTS) {
          rec.variants.push(vid);
        }
        if (typeof it.final_line_price === 'number') added += it.final_line_price;
      }
      /* Optimistic: the NEXT page load overwrites this with the authoritative Liquid cart total. */
      if (added > 0) touchCartValue((rec.cart_value || 0) + added);

      save();
      ga('ez_cart_add', {
        ez_cart_value: rec.cart_value,
        ez_add_count: rec.adds,
        ez_variant_id: rec.variants.length ? rec.variants[rec.variants.length - 1] : ''
      });
    } catch (e) {}
  }

  function wrapFetch() {
    if (typeof window.fetch !== 'function' || window.fetch.__ezcm) return;
    var original = window.fetch;
    var wrapped = function () {
      var promise = original.apply(this, arguments);
      try {
        var url = arguments[0];
        if (isAddUrl(url) && promise && typeof promise.then === 'function') {
          /* OBSERVE ONLY. The caller receives `promise` untouched; this branch clones the response
             so the body is never consumed out from under the theme's own handler. */
          promise
            .then(function (res) {
              if (!res || !res.ok) return;
              try {
                res
                  .clone()
                  .json()
                  .then(function (data) {
                    recordAdd(data);
                  })
                  .catch(function () {
                    recordAdd(null);
                  });
              } catch (e) {
                recordAdd(null);
              }
            })
            .catch(function () {});
        }
      } catch (e) {}
      return promise;
    };
    wrapped.__ezcm = true;
    window.fetch = wrapped;
  }

  /* No-JS / non-fetch fallback: a real form post to /cart/add. */
  function wireFormFallback() {
    document.addEventListener(
      'submit',
      function (e) {
        try {
          var f = e.target;
          if (f && f.tagName === 'FORM' && isAddUrl(f.getAttribute('action') || '')) recordAdd(null);
        } catch (err) {}
      },
      true
    );
  }

  /* ------------------------------------------------- checkout + purchase state */

  function wireCheckoutIntent() {
    document.addEventListener(
      'click',
      function (e) {
        try {
          if (!rec || rec.checkout_started) return;
          var t = e.target && e.target.closest ? e.target.closest('[name="checkout"], a[href*="/checkout"], a[href*="/checkouts/"]') : null;
          if (!t) return;
          rec.checkout_started = true;
          rec.checkout_started_at = new Date().toISOString();
          save();
        } catch (err) {}
      },
      true
    );
  }

  /* PURCHASE SUPPRESSION -- anyone who bought must never see the rescue.
     Shopify EMPTIES the cart on order completion and LEAVES IT INTACT on an abandoned checkout.
     So "this device went to checkout" AND "its server-side cart is now empty" is a strong completed
     -order signal that needs no admin access and no pixel. Two authoritative belts sit alongside it:
     a logged-in customer's own last-order date, and the KEY_PURCHASED flag a Custom Pixel can set. */
  function resolvePurchase(cartCount) {
    if (!rec || rec.purchased) return;
    var why = '';

    try {
      if (store && store.getItem(KEY_PURCHASED) === '1') why = 'pixel';
    } catch (e) {}

    if (!why && CFG && CFG.customer_last_order_day && CFG.sale_start_day) {
      if (CFG.customer_last_order_day >= CFG.sale_start_day) why = 'customer_order';
    }

    if (!why && rec.checkout_started && cartCount === 0 && (rec.adds > 0 || rec.max_cart_value > 0)) {
      why = 'checkout_then_empty_cart';
    }

    if (!why) return;
    rec.purchased = true;
    rec.purchased_at = new Date().toISOString();
    rec.purchased_via = why;
    save();
    ga('ez_purchase_suppressed', { ez_suppress_reason: why, ez_cart_value: rec.cart_value });

    /* HEURISTIC discount-code-on-purchase tie-through -- see this file's header for exactly what
       this is and is not. Only fires when this device actually carried a code; a purchase with no
       discount on it stays silent here (it is already fully represented by the event above). */
    if (rec.last_discount_codes && rec.last_discount_codes.length) {
      ga('ez_discount_purchase_heuristic', {
        ez_discount_code: rec.last_discount_codes.join(','),
        ez_suppress_reason: why,
        ez_cart_value: rec.cart_value
      });
    }
  }

  /* --------------------------------------------------------------- the popup */

  /* 15 Aug amendment -- see the header of snippets/ez-cart-memory.liquid for the owner's ruling.
     WHAT LEFT: the audience gate. It used to require a record first seen on or before the
     half-price day, not purchased, whose live cart already cleared the floor. The ads are pulling
     far more people than that pool and they all arrive knowing about the 50%, so the popup is now
     open to everyone who reaches the cart with something in it.
     WHAT STAYED, and why each one is not an audience gate:
       - rescue_open / phase3 window  -> the hard 18 Aug expiry, checked in two independent places.
       - is_cart + cart_count > 0     -> this surface only makes sense where there is an order to
                                         put a code on. Storewide awareness is the announcement bar.
       - min_cart > 0                 -> FAIL CLOSED. An unset threshold must never leak a discount,
                                         and the copy would read "orders over Rs 0".
       - code on cart (LIVE, DOM)    -> do not re-offer what is already on. Re-read every call, so
                                         removing the code brings both surfaces straight back.
       - cart_total >= min_cart      -> THE QUALIFYING GATE. See the block directly below.

     GONE 15 Aug (second owner ruling, same night): rescue_applied, max_shows and once-per-session.
     All three were sticky suppressions and the owner ruled the popup uncapped -- every qualifying
     cart visit shows it. See the comments on eligible() and inside qualifies().

     THE QUALIFYING GATE (owner ruling, 15 Aug, final -- "we not gonna show nothing to ones whose
     carts ain't above" the line). This REVERSES the first cut of this amendment, which deliberately
     showed the popup to under-floor carts so they would learn what the code needed. The owner ruled
     the opposite: a shopper who cannot use the code is not offered it.

     WHICH TOTAL, AND WHY IT IS THE RIGHT ONE. He expressed the line as Rs 4,070 -- the PRE-discount
     cart that becomes Rs 3,500 after the automatic 14% ("on which they can stack up 14% plus the
     1400 off"). Rs 4,070 and Rs 3,500 are the SAME gate stated from two sides, and this file uses
     the post-14% side:

         CFG.cart_total  = cart.total_price          (Liquid, POST automatic discounts)
         CFG.min_cart    = ez_cm_min_cart * 100      (the setting, Rs 3,500)

     That is apples to apples with what Shopify itself evaluates -- proven on live 15 Aug: a cart at
     Rs 2,750.28 post-14% was REFUSED and one at Rs 15,650.28 post-14% was ACCEPTED, against the
     3,500 floor. Comparing the PRE-discount total against a derived 4,070 would give the same answer
     ONLY while every item carries exactly 14%; the moment one product is excluded from the automatic
     discount, or the rate changes, the derived number silently drifts and starts hiding the offer
     from people who qualify. The post-14% comparison cannot drift, because it is the same quantity
     Shopify tests. It also needs NO second number anywhere: there is no 4,070 written in this
     codebase to fall out of sync with the 3,500 setting, which is the failure that cost the 14th.

     Because this gate now guarantees the cart qualifies, applyCode()'s below-minimum branch should
     be unreachable in practice. It is KEPT deliberately as a safety net: if it ever fires, the gate
     is wrong, and ez_failure_reason='below_minimum' is the signal that says so. */
  /* SPLIT 15 Aug, when the owner named the second surface ("with the discount bar over discount
     area. and popup"). qualifies() is the WHOLE money/window/cart gate and is shared VERBATIM by
     both surfaces, so there is exactly ONE copy of the threshold rule in this file and the bar can
     never disagree with the popup about who may have the code. eligible() is qualifies() plus the
     POPUP-ONLY frequency caps -- a bar is not an interruption, it sits in the page like any other
     cart row, so it carries no max_shows and no once-per-session.

     NO NEW ELIGIBILITY LOGIC ENTERED HERE. Every line below was already in eligible(); the only
     change is which of the two functions each line landed in.

     `total` is optional and defaults to the server-rendered CFG.cart_total. It exists so the
     re-mount path after a quantity change can re-test against a freshly fetched /cart.js total --
     the SAME quantity (cart.total_price, post automatic discounts), never a client-side derivation. */
  function qualifies(total) {
    if (!CFG || !CFG.rescue_enabled) return false;
    if (CFG.rescue_open === false) return false; /* Liquid-side PK-calendar-day gate */
    if (!CFG.is_cart) return false; /* cart page only */
    if (!(CFG.cart_count > 0)) return false;
    if (!(CFG.min_cart > 0)) return false; /* FAIL CLOSED: unset/zero threshold renders nothing */
    if (!CFG.code) return false;

    /* the qualifying gate -- post-14% cart total against the setting. See the block above. */
    var t = typeof total === 'number' ? total : CFG.cart_total;
    if (!(typeof t === 'number' && t >= CFG.min_cart)) return false;
    if (!rec) return false; /* boot() seeds one for any non-empty cart, so this is never the gate */

    var d = today();
    if (!(d >= CFG.phase3_start && d <= CFG.phase3_end)) return false;

    /* THE ONLY "already has it" TEST, and it is LIVE (owner ruling, 15 Aug: "removing the code
       removes the application banner as well ... so keep em synced if not applied and above floor
       show it up").

       WHAT WAS REMOVED AND WHY. Two gates used to sit here and BOTH were sticky in the wrong
       direction, so a shopper who applied the code and then removed it could never get it back:
         1. `rec.rescue_applied` -- a localStorage flag set on a successful apply and never
            cleared. Removing the code cannot clear a flag on the device, so this one latched
            FOREVER, across every later visit. Gone. (The field is still WRITTEN, untouched, as
            measurement -- the storage contract is not changed, only what decides the display.)
         2. `CFG.cart_discount_codes` -- correct, but rendered by Liquid at PAGE LOAD. Removing a
            code re-renders the cart summary via morphSection with NO page load, so this array
            still said the code was on and the bar refused to come back.
       Both are replaced by codesOnCart(), which reads the theme's OWN rendered pills. That is the
       same list the shopper is literally looking at, and it is re-read on every call, so the bar's
       visibility is a pure function of (qualifies AND not applied), re-evaluated every render.
       Falls back to the server-rendered array if the pill host is not in the DOM. */
    try {
      var on = codesOnCart();
      for (var i = 0; i < on.length; i++) {
        if (String(on[i]).toUpperCase() === String(CFG.code).toUpperCase()) return false;
      }
    } catch (e) {}

    return true;
  }

  /* THE CODES ON THE CART RIGHT NOW. DOM first (the theme's own pills, always current, survives a
     morphSection with no page load), server-rendered array as the fallback for the case where the
     discount disclosure is not in the document at all. */
  function codesOnCart() {
    try {
      if (document.querySelector('accordion-custom.cart-discount, .cart-discount')) {
        var out = [];
        var pills = document.querySelectorAll('.cart-discount__pill');
        for (var i = 0; i < pills.length; i++) {
          var c = pills[i].getAttribute('data-discount-code');
          if (c) out.push(c);
        }
        return out;
      }
    } catch (e) {}
    return Array.isArray(CFG.cart_discount_codes) ? CFG.cart_discount_codes : [];
  }

  /* THE POPUP IS UNCAPPED (owner ruling, 15 Aug, verbatim: "the popup shows always not just closed
     for the guy who have dismissed it once ... everytime one hits the cart tell him. not capped and
     limited to one and stuff like that").

     So eligible() IS qualifies(), with nothing added. Two gates were removed:
       - `rescue_shown >= max_shows`  -- a lifetime cap on the device.
       - the SESSION_SHOWN sessionStorage read -- one-per-session suppression.
     Neither key is deleted and BOTH are still written exactly as before: the storage contract is
     untouched (other things read ez_cm_*), only the show LOGIC changed. rescue_shown remains an
     honest count of how many times it was shown, which is now the more interesting number.

     What still holds the popup back is all inside qualifies(): the code already being on the cart
     (nothing to offer), a cart under the floor, an empty cart, a page that is not /cart, and the
     18 Aug expiry. Plus render()'s own one-per-document guard, so a single cart view still shows
     it once, not twice. */
  function eligible() {
    return qualifies();
  }

  /* ------------------------------------------------------- the one-tap apply */

  /* The PERMANENT, generic discount-surface instrumentation (snippets/ez-discount-tracking.liquid,
     mounted site-wide in <head> via ez-shell.liquid, NOT deferred -- so it is always defined by the
     time this deferred file runs). Same ez_device_id join key as every other discount event, and
     the same generic vocabulary the 14 Aug cart bar used, so the popup's numbers sit in one series
     across both promos rather than starting a new one. Guarded: instrumentation never throws into a
     money surface. */
  function dtrack(name, params) {
    try {
      if (typeof window.ezDiscountTrack === 'function') window.ezDiscountTrack(name, params);
    } catch (e) {}
  }

  /* The codes already on the cart, read off the theme's own rendered pills -- the exact payload
     assets/cart-discount.js builds. Applying must never silently drop a code the shopper already
     has on. */
  function existingCodes() {
    var out = [];
    try {
      var pills = document.querySelectorAll('.cart-discount__pill');
      for (var i = 0; i < pills.length; i++) {
        var c = pills[i].getAttribute('data-discount-code');
        if (c) out.push(c);
      }
    } catch (e) {}
    return out;
  }

  function svgClose() {
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" ' +
      'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
    );
  }

  /* ---- THE ONE APPLY. Hoisted to module scope 15 Aug so the bar and the popup share it. ----
     This function was previously nested inside render(). It was LIFTED, not rewritten: the POST,
     the applicable check, the reload, the below_minimum branch and every tracking call are the
     ones the popup already shipped with and browser-ops verified firing on live. The ONLY change
     is that `ez_surface` is now a parameter instead of the literal 'popup', so both surfaces write
     into one series. There is deliberately no second copy of any of this. */
  var applying = false;

  function applyDiscount(opts) {
    var btn = opts.btn;
    var surface = opts.surface;
    var showErr = opts.showErr || function () {};

    if (applying) return;
    applying = true;
    try { btn.setAttribute('aria-busy', 'true'); } catch (e) {}

    dtrack('ez_discount_popup_tap', { ez_discount_code: CFG.code, ez_surface: surface });
    try {
      ga('ez_rescue_applied', { ez_cart_value: rec ? rec.cart_value : 0, ez_discount_code: CFG.code });
    } catch (e) {}

    function done() {
      applying = false;
      try { btn.removeAttribute('aria-busy'); } catch (e) {}
    }

    var url = CFG.cart_update_url;
    if (!url || typeof window.fetch !== 'function') {
      /* last-resort fallback: Shopify's own /discount/CODE route, which applies and redirects.
         Still one tap, still no typing. Only reachable if routes failed to render or fetch is
         unavailable, neither of which happens on a normal cart page. */
      if (CFG.apply_url) {
        dtrack('ez_discount_apply_result', {
          ez_discount_code: CFG.code,
          ez_surface: surface,
          ez_result: 'redirect',
          beacon: true
        });
        window.location.assign(CFG.apply_url);
        return;
      }
      done();
      return;
    }

    var codes = existingCodes();
    if (codes.indexOf(CFG.code) === -1) codes.push(CFG.code);

    window
      .fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var list = (data && data.discount_codes) || [];
        var mine = null;
        for (var i = 0; i < list.length; i++) {
          if (String(list[i].code).toUpperCase() === String(CFG.code).toUpperCase()) mine = list[i];
        }
        if (mine && mine.applicable) {
          try {
            if (rec) {
              rec.rescue_applied = true;
              save();
            }
          } catch (e) {}
          /* beacon: a navigation follows immediately, so the hit must not lose the unload race */
          dtrack('ez_discount_apply_result', {
            ez_discount_code: CFG.code,
            ez_surface: surface,
            ez_result: 'success',
            beacon: true
          });
          window.location.assign(CFG.cart_url || '/cart');
          return;
        }

        var under = typeof CFG.cart_total === 'number' && CFG.min_cart > 0 && CFG.cart_total < CFG.min_cart;
        dtrack('ez_discount_apply_result', {
          ez_discount_code: CFG.code,
          ez_surface: surface,
          ez_result: 'failure',
          ez_failure_reason: under ? 'below_minimum' : 'not_applicable'
        });
        showErr(
          under
            ? CFG.code + ' applies to orders over Rs ' + (CFG.min_label || '') + '. Your cart is under that right now.'
            : CFG.code + ' did not apply to this cart. You can enter it in the box below.'
        );
        done();
      })
      .catch(function () {
        dtrack('ez_discount_apply_result', {
          ez_discount_code: CFG.code,
          ez_surface: surface,
          ez_result: 'failure',
          ez_failure_reason: 'network_error'
        });
        showErr('That did not go through. Check your connection and try again.');
        done();
      });
  }

  /* ------------------------------------------------ the in-cart one-tap bar */

  /* THE SECOND SURFACE (owner, 15 Aug: "with the discount bar over discount area. and popup").
     It sits directly above the theme's own discount-code disclosure, which is exactly where the
     14 Aug AZADI50 bar sat and where a shopper already looks for a code box.

     WHY IT IS MOUNTED FROM JS AND NOT FROM cart-summary.liquid. The 14 Aug bar was a Liquid
     snippet rendered from cart-summary.liquid. Doing that again would mean pushing
     cart-summary.liquid, a shared cart file this change has no other business touching. The
     anchor below is the theme's own `accordion-custom.cart-discount` element -- the same node the
     14 Aug snippet was rendered immediately before -- so the placement is identical without
     putting a shared file in the blast radius.

     IT CARRIES NO ELIGIBILITY OF ITS OWN. qualifies() is the single gate; see the split above. */
  function barMarkup() {
    var line = String(CFG.bar_line || CFG.body || '')
      .replace(/\[code\]/g, CFG.code)
      .replace(/\[min\]/g, CFG.min_label || '');

    return (
      '<div class="ezcmbar__in">' +
      '<p class="ezcmbar__text">' + esc(line) + '</p>' +
      '<div class="ezcmbar__row">' +
      '<span class="ezcmbar__chip">' + esc(CFG.code) + '</span>' +
      '<button type="button" class="ezcmbar__go" data-ezcmbar-apply>' + esc(CFG.cta || 'Apply') + '</button>' +
      '</div>' +
      '<p class="ezcmbar__err" data-ezcmbar-err hidden role="alert"></p>' +
      '</div>'
    );
  }

  function barAnchor() {
    try {
      return (
        document.querySelector('accordion-custom.cart-discount') ||
        document.querySelector('.cart-discount')
      );
    } catch (e) {
      return null;
    }
  }

  function renderBar() {
    if (document.getElementById('ezcmbar')) return;
    var anchor = barAnchor();
    if (!anchor || !anchor.parentNode) return;

    var host = document.createElement('div');
    host.className = 'ezcmbar';
    host.id = 'ezcmbar';
    host.innerHTML = barMarkup();
    anchor.parentNode.insertBefore(host, anchor);

    function showErr(msg) {
      try {
        var slot = host.querySelector('[data-ezcmbar-err]');
        if (!slot) return;
        slot.textContent = msg;
        slot.removeAttribute('hidden');
      } catch (e) {}
    }

    host.addEventListener('click', function (e) {
      var apply = e.target.closest ? e.target.closest('[data-ezcmbar-apply]') : null;
      if (!apply) return;
      e.preventDefault();
      applyDiscount({ surface: 'bar', btn: apply, showErr: showErr });
    });

    /* The BAR's own impression event. Renamed 15 Aug from ez_discount_popup_shown to the documented
       name in craft/engineer/measurement-inventory.md -- a bar impression written under the popup's
       event name made the two surfaces indistinguishable in any report that reads by event rather
       than by parameter. ez_surface stays exactly as it was ('bar'), because downstream reads by
       surface and must keep working through the rename. */
    dtrack('ez_discount_bar_shown', { ez_discount_code: CFG.code, ez_surface: 'bar' });
  }

  /* The theme replaces the whole cart summary on a quantity change, which takes the bar with it.
     Re-mount when the anchor comes back, re-testing the money gate against a FRESHLY FETCHED
     /cart.js total rather than the now-stale server-rendered one. Same quantity Shopify evaluates
     (total_price, post automatic discounts) -- no derivation, no second threshold. Fail-silent. */
  function watchBar() {
    if (typeof MutationObserver !== 'function') return;
    var pending = false;

    var obs = new MutationObserver(function () {
      if (pending) return;
      if (document.getElementById('ezcmbar')) return;
      if (!barAnchor()) return;
      pending = true;

      var settle = function () { pending = false; };

      if (typeof window.fetch !== 'function') {
        settle();
        return;
      }
      window
        .fetch((CFG.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          try {
            CFG.cart_count = typeof c.item_count === 'number' ? c.item_count : CFG.cart_count;
            CFG.cart_total = typeof c.total_price === 'number' ? c.total_price : CFG.cart_total;
            if (qualifies()) renderBar();
          } catch (e) {}
          settle();
        })
        .catch(settle);
    });

    try {
      obs.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  function render(cartTotal) {
    if (document.getElementById('ezcm')) return;

    var returnFocusTo = document.activeElement;
    var host = document.createElement('div');
    host.className = 'ezcm';
    host.id = 'ezcm';
    host.setAttribute('role', 'dialog');
    /* No aria-modal: this surface deliberately does NOT focus-trap (owner instruction), and claiming
       modality to assistive tech while leaving the page reachable would be a lie. */
    host.setAttribute('aria-labelledby', 'ezcm-title');

    /* [code] and [min] are the two merchant tokens. [min] renders the SAME setting the eligibility
       and the failure line read, so the number in the sentence can never drift from the number the
       discount actually enforces. It is stated as the shopper's own cart total (post the automatic
       14%), which is what Shopify evaluates the minimum against. */
    var body = String(CFG.body || '')
      .replace(/\[code\]/g, CFG.code)
      .replace(/\[min\]/g, CFG.min_label || '');

    host.innerHTML =
      '<div class="ezcm__backdrop" data-ezcm-close></div>' +
      '<div class="ezcm__wrap" data-ezcm-surround>' +
      '<div class="ezcm__panel">' +
      '<button type="button" class="ezcm__close" data-ezcm-close aria-label="' +
      esc(CFG.close_label || 'Close') +
      '">' +
      svgClose() +
      '</button>' +
      (CFG.eyebrow ? '<p class="ezcm__eyebrow">' + esc(CFG.eyebrow) + '</p>' : '') +
      '<h2 class="ezcm__title" id="ezcm-title">' + esc(CFG.heading) + '</h2>' +
      '<p class="ezcm__body">' + esc(body) + '</p>' +
      '<div class="ezcm__meta"><p class="ezcm__code"><span>' + esc(CFG.code) + '</span></p>' +
      (CFG.ends ? '<span class="ezcm__ends">' + esc(CFG.ends) + '</span>' : '') +
      '</div>' +
      '<button type="button" class="ezcm__cta" data-ezcm-apply>' + esc(CFG.cta) + '</button>' +
      '<p class="ezcm__err" data-ezcm-err hidden role="alert"></p>' +
      '<button type="button" class="ezcm__dismiss" data-ezcm-close>' + esc(CFG.dismiss) + '</button>' +
      '</div></div>';

    document.body.appendChild(host);
    document.documentElement.classList.add('ezcm-locked');

    var closed = false;
    function close(reason) {
      if (closed) return;
      closed = true;
      try {
        document.documentElement.classList.remove('ezcm-locked');
        if (host.parentNode) host.parentNode.removeChild(host);
        document.removeEventListener('keydown', onKey, true);
        if (reason === 'dismiss') {
          if (rec) {
            rec.rescue_dismissed = (rec.rescue_dismissed || 0) + 1;
            save();
            ga('ez_rescue_dismissed', { ez_shown_count: rec.rescue_shown, ez_cart_value: rec.cart_value });
          }
          dtrack('ez_discount_popup_dismissed', { ez_discount_code: CFG.code, ez_surface: 'popup' });
        }
        if (returnFocusTo && returnFocusTo.focus) returnFocusTo.focus();
      } catch (e) {
        /* last-resort: never leave the page scroll-locked */
        try { document.documentElement.classList.remove('ezcm-locked'); } catch (e2) {}
      }
    }

    function onKey(e) {
      if (e.key === 'Escape' || e.keyCode === 27) close('dismiss');
    }

    /* ---- ONE TAP APPLIES IT. No code to read, no box to find, nothing to type. -------------
       This is the exact mechanism the 14 Aug cart bar shipped with and browser-ops verified on
       live: POST the existing pill codes plus ours to cart/update, and on applicable:true RELOAD
       the cart so the pill, the totals and this popup's own disappearance all come from the
       SERVER. Nothing on a money surface is drawn from a client-side guess.
       On applicable:false we say the honest thing, naming the threshold when that is what is in
       the way. That failure signal is the valuable half -- a successful-redemption count can never
       show "fifty people tried and were refused". */
    function showErr(msg) {
      try {
        var slot = host.querySelector('[data-ezcm-err]');
        if (!slot) return;
        slot.textContent = msg;
        slot.removeAttribute('hidden');
      } catch (e) {}
    }

    host.addEventListener('click', function (e) {
      var apply = e.target.closest ? e.target.closest('[data-ezcm-apply]') : null;
      if (apply) {
        e.preventDefault();
        applyDiscount({ surface: 'popup', btn: apply, showErr: showErr });
        return;
      }
      if (e.target.closest && e.target.closest('[data-ezcm-close]')) {
        e.preventDefault();
        close('dismiss');
        return;
      }
      /* the panel's empty surround behaves as the backdrop under it does */
      if (e.target === host.querySelector('[data-ezcm-surround]')) close('dismiss');
    });

    document.addEventListener('keydown', onKey, true);
    /* Belt against a frozen page: the lock never outlives the document. */
    window.addEventListener('pagehide', function () {
      try { document.documentElement.classList.remove('ezcm-locked'); } catch (e) {}
    });

    try {
      var btn = host.querySelector('.ezcm__close');
      if (btn && btn.focus) btn.focus({ preventScroll: true });
    } catch (e) {}

    rec.rescue_shown = (rec.rescue_shown || 0) + 1;
    rec.rescue_last_shown = new Date().toISOString();
    save();
    try { if (session) session.setItem(SESSION_SHOWN, '1'); } catch (e) {}

    ga('ez_rescue_shown', {
      ez_cart_value: cartTotal,
      ez_shown_count: rec.rescue_shown,
      ez_accessed_on_14: rec.accessed_on_14 ? 'yes' : 'no',
      ez_first_seen_day: rec.first_seen_day
    });
    /* Both series fire, on purpose. ez_rescue_* is the cart-memory funnel (who came back, from
       which day, with what cart). ez_discount_popup_* is the PERMANENT discount-surface funnel the
       14 Aug bar and popup wrote into -- keeping it means shown/tap/dismissed/apply_result stays
       one continuous series across both promos instead of restarting. */
    dtrack('ez_discount_popup_shown', { ez_discount_code: CFG.code, ez_surface: 'popup' });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ------------------------------------------------------------------- boot */

  function boot() {
    var node = document.getElementById('ez-cm-config');
    if (!node) return;
    try {
      CFG = JSON.parse(node.textContent || '{}');
    } catch (e) {
      return;
    }
    if (!CFG || CFG.active !== true) return;

    store = probe('localStorage');
    session = probe('sessionStorage');

    var cartCount = typeof CFG.cart_count === 'number' ? CFG.cart_count : 0;
    var cartTotal = typeof CFG.cart_total === 'number' ? CFG.cart_total : 0;

    rec = read();

    /* Phase 1 -- the tap. Wired before anything else so a fast add is never missed. */
    wrapFetch();
    wireFormFallback();

    wireCheckoutIntent();

    /* Back-fill: Shopify's own 14-day cart proves this device has an abandoned cart even when we
       never saw the add (it happened before this shipped, or in a cleared storage). */
    if (!rec && cartCount > 0) seed('cart_seed', cartTotal);

    if (rec) {
      touchCartValue(cartTotal);
      touchDiscountCodes(Array.isArray(CFG.cart_discount_codes) ? CFG.cart_discount_codes : []);

      /* Phase 2 -- the 14 Aug tag. Explicitly asked for so the funnel is measurable. */
      if (today() === CFG.phase2_date && !rec.accessed_on_14) {
        rec.accessed_on_14 = true;
        rec.accessed_on_14_at = new Date().toISOString();
        save();
        ga('ez_return_on_14', {
          ez_cart_value: rec.cart_value,
          ez_first_seen_day: rec.first_seen_day,
          ez_add_count: rec.adds || 0
        });
      }

      resolvePurchase(cartCount);
      save();

      /* Phase 3, surface 1 -- THE BAR, above the discount box. Mounted immediately (it is part of
         the page, not an interruption) and re-mounted after a quantity change. Same gate as the
         popup minus the popup-only frequency caps. */
      if (qualifies()) renderBar();
      if (CFG.is_cart) watchBar();

      /* Phase 3, surface 2 -- the popup. Deferred a beat so it never competes with LCP. */
      if (eligible()) {
        window.setTimeout(function () {
          try {
            if (eligible()) render(cartTotal);
          } catch (e) {}
        }, CFG.delay_ms || 1400);
      }
    }
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (e) {
    /* silent by design -- see the header */
  }
})();
